import { fetchHealth } from "../services/forecastServerApi.js";
import { useState, useEffect, useRef } from "react";
import {
  applyForecasters,
  FORECASTER_INFO,
  persistForecasterCache,
  HIDDEN_TIERS,
  retiredEngineTag,
  tagOwner,
  isPrimaryEngine,
  engineAfterMirabilisFlip,
  mirabilisLanesEnabled,
  setMirabilisLanesEnabled,
} from "../lib/forecast/engines.js";

/**
 * Forecast-engine selection domain: compareEngine, forecastEngine and
 * orchestrators state with localStorage persistence effects and the
 * /api/forecast/health discovery effect.
 *
 * Includes engine validation and legacy-alias migration logic.
 *
 * @returns {{ compareEngine: string|null, setCompareEngine: Function, forecastEngine: string, setForecastEngine: Function, mirabilisLanes: boolean, setMirabilisLanes: Function, orchestrators: string[], setOrchestrators: Function, triggerLlm: string|null, setTriggerLlm: Function, compareTriggerLlm: string|null, setCompareTriggerLlm: Function }}
 */
export function useEngineSelection() {
  // Secondary ("Engine B") selection for dual-engine compare. Persisted like
  // forecastEngine so a reload restores the user's chosen second engine instead
  // of falling back to the auto-flipped default (bug: secondary engine lost on
  // reload). null = "auto" (flip the primary's tag); a stored value pins it.
  const [compareEngine, setCompareEngine] = useState(() => {
    try {
      return localStorage.getItem("sooth-compare-engine") || null;
    } catch (_e) {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (compareEngine) localStorage.setItem("sooth-compare-engine", compareEngine);
      else localStorage.removeItem("sooth-compare-engine");
    } catch (_e) {
      /* localStorage unavailable */
    }
  }, [compareEngine]);
  // Per-leg trigger-LLM (postprocess) override for dual-engine compare.
  // Value is one of: null (server default) | a non-empty model id | the
  // "__custom__" sentinel (custom mode selected, no id typed yet). Only a
  // concrete id is persisted — null and the transient "__custom__" sentinel
  // both mean "no override" and are cleared from storage (so a reload starts
  // from server default, never a half-entered custom state).
  // triggerLlm = Engine A leg; compareTriggerLlm = Engine B leg.
  const [triggerLlm, setTriggerLlm] = useState(() => {
    try {
      return localStorage.getItem("sooth-trigger-llm") || null;
    } catch (_e) {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (triggerLlm && triggerLlm !== "__custom__")
        localStorage.setItem("sooth-trigger-llm", triggerLlm);
      else localStorage.removeItem("sooth-trigger-llm");
    } catch (_e) {
      /* localStorage unavailable */
    }
  }, [triggerLlm]);
  const [compareTriggerLlm, setCompareTriggerLlm] = useState(() => {
    try {
      return localStorage.getItem("sooth-compare-trigger-llm") || null;
    } catch (_e) {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (compareTriggerLlm && compareTriggerLlm !== "__custom__")
        localStorage.setItem("sooth-compare-trigger-llm", compareTriggerLlm);
      else localStorage.removeItem("sooth-compare-trigger-llm");
    } catch (_e) {
      /* localStorage unavailable */
    }
  }, [compareTriggerLlm]);
  // Persist across reloads — without this, `forecastEngine` resets to "" on
  // every page reload and stays empty until /api/forecast/health responds.
  // The chat input is gated behind a non-empty engine, but timeline branch
  // triggers aren't — a click in that ~5-15s gap fired a branch with an
  // empty `orchestrator` (see bug 8e8e8866). Persisting + hydrating
  // synchronously closes the window. Health response below still validates
  // the persisted value against the live orchestrators list and falls back
  // to a preferred default if the engine has been retired.
  const [forecastEngine, setForecastEngine] = useState(() => {
    try {
      return localStorage.getItem("sooth-forecast-engine") || "";
    } catch (_e) {
      return "";
    }
  });
  useEffect(() => {
    try {
      if (forecastEngine) localStorage.setItem("sooth-forecast-engine", forecastEngine);
      else localStorage.removeItem("sooth-forecast-engine");
    } catch (_e) {
      /* localStorage unavailable */
    }
  }, [forecastEngine]);
  const [orchestrators, setOrchestrators] = useState([]); // from GCP health endpoint
  // Cloud /health permanently failed (all retries exhausted). Drives the first-run
  // error+retry affordance in the composer: without this, a first-time visitor whose
  // /health never returns is stranded FOREVER on "Initializing server connection…" —
  // the retry loop just goes quiet with `forecastEngine` left "" (#3528). Returning
  // users hydrate an engine from localStorage and never hit that branch.
  const [healthError, setHealthError] = useState(false);
  // Bumped by retryHealth() to re-run the discovery effect (manual retry button).
  const [healthNonce, setHealthNonce] = useState(0);
  const retryHealth = () => {
    setHealthError(false);
    setHealthNonce((n) => n + 1);
  };

  // Fetch available forecasters with retry on failure (1s, 2s, 4s, 8s, 16s)
  useEffect(() => {
    let cancelled = false;
    // Returns true on success, false if every retry was exhausted — the caller uses the
    // cloud fetch's result to arm the first-run error state.
    const fetchWithRetry = async (url, setter, retries = 5, delay = 1000) => {
      for (let i = 0; i <= retries && !cancelled; i++) {
        try {
          // fetch() resolves (doesn't throw) on HTTP 4xx/5xx, so the proxy's
          // 502 ("Forecast server unreachable", emitted on backend cold-start /
          // unavailability) must be turned into a throw — otherwise we'd parse
          // the error JSON, skip the setter, and return WITHOUT retrying,
          // defeating the backoff for the exact case it exists to ride out.
          const r = await fetchHealth(url);
          if (!r.ok) throw new Error(`health ${r.status}`);
          const d = await r.json();
          // A 200 whose body carries neither array is an unexpected shape (not a
          // healthy contract response) — treat it like the 502 above and throw so
          // the backoff retries, instead of returning with forecastEngine left
          // unset (which strands the UI on "Initializing server connection…").
          if (!Array.isArray(d.forecasters) && !Array.isArray(d.orchestrators))
            throw new Error("health: unexpected shape");
          if (!cancelled) setter(d);
          return true;
        } catch (_e) {
          if (i < retries && !cancelled)
            await new Promise((r) => setTimeout(r, delay * Math.pow(2, i)));
        }
      }
      return false;
    };
    // Arm the error state only if the fetch permanently fails.
    fetchWithRetry("/api/forecast/health", (d) => {
      applyForecasters(FORECASTER_INFO, d.forecasters);
      persistForecasterCache(); // cache for instant display_name resolution on next reload
      // Drop hidden tiers (Deep) before anything downstream sees the list, so
      // the selector never offers them and the default-selection below migrates
      // a persisted hidden engine to a visible default. (Debug compare
      // dropdowns read the full registry directly, not this list.)
      const list = (d.forecasters || [])
        .filter((f) => !(f.tag && HIDDEN_TIERS.has(f.tag)))
        .map((f) => f.name);
      setOrchestrators(list);
      // Functional updater — this callback closes over forecastEngine at
      // first-render and the response can arrive much later (multi-second
      // backoff). Reading `cur` straight off the state guarantees we
      // validate against whatever the user has since selected, not a stale
      // closure value.
      const hasPrimary = list.some(isPrimaryEngine);
      setForecastEngine((cur) => {
        // Persisted pre-#447 wire aliases (sooth_agent_fast/pro) are no longer
        // served — migrate them to the forecaster now carrying their tag.
        const legacyTag = retiredEngineTag(cur);
        if (legacyTag) {
          const owner = tagOwner(legacyTag);
          if (owner && list.includes(owner)) return owner;
        }
        // Keep the current engine only if it's in the health list AND still a
        // user-visible tier default. A plain `list.includes(cur)` would
        // silently KEEP a persisted engine that's no longer in the selector
        // (a retired `local:` engine included) — migrate those to a tier
        // default instead. (When the deployment exposes no tagged forecasters
        // at all, keep whatever's valid so degraded backends still work.)
        if (cur && list.includes(cur) && (isPrimaryEngine(cur) || !hasPrimary)) return cur;
        // Prefer the user-visible tier defaults (Quick → Deep) before
        // falling back to whatever the server returned first.
        const quick = tagOwner("quick"),
          deep = tagOwner("deep");
        const preferred =
          quick && list.includes(quick) ? quick : deep && list.includes(deep) ? deep : list[0];
        return preferred || cur;
      });
    }).then((ok) => {
      // Cloud health never came back after the full backoff — surface a retry affordance
      // instead of an eternal "Initializing…" pulse (#3528). Only meaningful for a first-time
      // visitor (no persisted engine); a returning user already has one and never sees it.
      if (!ok && !cancelled) setHealthError(true);
    });
    return () => {
      cancelled = true;
    };
    // Re-runs when retryHealth() bumps the nonce (manual "Retry" from the first-run error).
  }, [healthNonce]);

  // Product spec: the customer-facing forecaster selection (the chat-box
  // Quick/Deep pill + the Settings segmented control, which share
  // `forecastEngine`) RESETS to Quick on every page load / re-login — the prior
  // session's choice is NOT restored. The `sooth-forecast-engine` persistence
  // above still runs so the value is non-empty synchronously on reload (closing
  // the empty-orchestrator branch-trigger race), but we override it to the Quick
  // owner. One-shot per mount (ref-guarded): in-session switches to Deep are
  // preserved; only a fresh load reverts.
  //
  // Resolve Quick from the *cached* forecaster registry (hydrated synchronously
  // from localStorage in engines.js), NOT the live orchestrators list — the
  // latter only lands after /health returns, and `forecastEngine` is hydrated
  // from localStorage synchronously, so gating on `orchestrators.includes(quick)`
  // left a window where a persisted Deep could drive a submission before the
  // reset fired. For any returning user (the only ones who can have a persisted
  // Deep) the cache is warm, so tagOwner("quick") resolves on mount and the
  // reset happens before that window opens. On a cold cache (first-ever load)
  // tagOwner is null, so this effect defers; it re-runs when `orchestrators`
  // arrives (FORECASTER_INFO is populated in the same /health callback), and the
  // health validator above independently migrates any unserved/hidden engine to
  // a visible default — so a stale cache self-corrects within ~1s.
  const quickResetRef = useRef(false);
  useEffect(() => {
    if (quickResetRef.current) return;
    const quick = tagOwner("quick");
    if (quick) {
      quickResetRef.current = true;
      setForecastEngine(quick);
    }
  }, [orchestrators]);

  // Debug Settings ▸ Engines (Mirabilis on by default). The value itself lives in the engines
  // module (tagOwner/effectiveTier are pure reads called during render); this state
  // exists only to re-render when it flips, which is why the module setter runs
  // FIRST — a render triggered before it would resolve the old owner.
  const [mirabilisLanes, setMirabilisLanesState] = useState(() => mirabilisLanesEnabled());
  const setMirabilisLanes = (on) => {
    setMirabilisLanesEnabled(on);
    setMirabilisLanesState(!!on);
    // An updater, not a value: it maps whatever engine is current when it runs, so a
    // page-load reset to Quick queued in the same commit is kept, not overwritten.
    setForecastEngine((cur) => engineAfterMirabilisFlip(cur, on) ?? cur);
  };

  return {
    compareEngine,
    setCompareEngine,
    forecastEngine,
    setForecastEngine,
    mirabilisLanes,
    setMirabilisLanes,
    orchestrators,
    setOrchestrators,
    triggerLlm,
    setTriggerLlm,
    compareTriggerLlm,
    setCompareTriggerLlm,
    healthError,
    retryHealth,
  };
}
